import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type UnsavedChangesCheck = () => Observable<boolean> | boolean;

@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
    private checkHandler: UnsavedChangesCheck | null = null;
    private allowNextNavigation = false; // Flag to allow the next navigation without showing the unsaved changes dialog

    /**
     * Registers a handler to check for unsaved changes.
     * 
     * @param handler The function to be called to check for unsaved changes. 
     * This function should return a boolean or an Observable that emits a boolean, 
     * where true means there are unsaved changes and false means there are not.
     */
    register(handler: UnsavedChangesCheck) {
        this.checkHandler = handler;
    }

    /**
     * Unregisters a handler to check for unsaved changes.
     * 
     * @param handler The function to be called to check for unsaved changes. 
     * This function should return a boolean or an Observable that emits a boolean, 
     * where true means there are unsaved changes and false means there are not.
     */
    unregister(handler: UnsavedChangesCheck) {
        if (this.checkHandler === handler) {
            this.checkHandler = null;
        }
    }

    /**
     * Checks if there are unsaved changes by calling the registered handler.
     * If no handler is registered, it returns false (no unsaved changes).
     * 
     * @returns A boolean or an Observable that emits a boolean indicating whether there are unsaved changes.
     */
    canNavigate(): Observable<boolean> | boolean {
        return this.checkHandler ? this.checkHandler() : true;
    }

    allowNextNavigationOnce() {
        this.allowNextNavigation = true;
    }

    consumeAllowNextNavigation(): boolean {
        const allow = this.allowNextNavigation;
        this.allowNextNavigation = false;
        return allow;
    }
}
